import {
  gtcScrollLock,
  gtcScrollUnlock,
  getPageContentHtml,
  getSectionHtml,
  getTemplateHtml,
  gtcDebounce,
  gtcTransitionEnd,
  gtcLoadScripts,
} from "gtcUtils";

const classes = {
  open: "open",
  close: "close",
  overlayOpen: "gtc-overlay-open",
  scrollLock: "gtc-scroll-lock",
  animate: "animate",
  overlay: "gtc-modal--overlay",
  showOverlay: "gtc-show-overlay",
};

/**
 * GtcModal
 * @class
 * @extends HTMLDialogElement
 * @description Custom modal dialog element
 * @property {string} data-close-outside - Close the modal when clicking outside the dialog
 * @property {string} data-scroll-lock - Lock the scroll when the modal is open
 * @property {string} data-open-inside - Open the modal inside the element with the specified query selector
 * @example
 * 
 * // Modal
 * <gtc-modal id="{{modalId}}" >
 *  <dialog class="gtc-modal {{class}}" data-close-outside data-scroll-lock data-open-inside="#my-element">
 *    <gtc-modal-content>
 *      <span slot="title">Custom Dialog</span>
 *      <div slot="body">Custom dialog content</div>
 *    </gtc-modal-content>  
 *  </dialog>
 * </gtc-modal>
 * 
 * // Trigger the modal
 * <button
 *     aria-controls="{{modalId}}"
 *     class="button button--primary"
 *     role="button"
 *     type="button"
 *     onclick="document.getElementById('{{modalId}}').open();"
 * >
 *     Trigger text
 * </button>
 */

class GtcModal extends HTMLElement {
  #disableScrollLock = false;
  #scriptsLoaded = false;

  constructor() {
    super();
    this.settings = {};
    this.open = this.open.bind(this);
    this.close = this.close.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);
    this.debouncedSetPosition = gtcDebounce(this.setPosition.bind(this), 100);
  }

  connectedCallback() {
    this.settings = { ...this.dataset };
    Object.keys(this.settings).forEach(key => {
      if (this.settings[key] === "") {
        this.settings[key] = true;
      }
    });

    if (this.settings.openInside) {
      this.settings.openInside = document.querySelector(this.settings.openInside) || null;
      if (!this.settings.openInside) {
        console.warn('No element found to open inside');
        return;
      }
    }

    this.setupDialog();
    this.setupContent();
    this.setAriaAttributes();
    // this.addEventListeners();
  }

  setupDialog() {
    this.dialog = this.querySelector('dialog');
    if (!this.dialog) {
      console.warn('No dialog element found inside gtc-modal');
      return;
    }
  }

  setupContent() {
    this.content = this.querySelector('gtc-modal-content');
    if (!this.content) {
      console.warn('No gtc-modal-content element found inside gtc-modal');
      return;
    }
  }

  disconnectedCallback() {
    this.removeOpenElementListeners();
  }

  addOpenElementListeners() {
    this.dialog.addEventListener('keydown', this.handleKeydown);
    if (this.settings.openInside) {
      requestAnimationFrame(() => {
        window.addEventListener('click', this.handleClick);
        window.addEventListener('resize', this.debouncedSetPosition);
      });
    } else {
      this.dialog.addEventListener('click', this.handleClick);
    }
  }

  removeOpenElementListeners() {
    this.dialog.removeEventListener('keydown', this.handleKeydown);
    if (this.settings.openInside) {
      requestAnimationFrame(() => {
        window.removeEventListener('click', this.handleClick);
        window.addEventListener('resize', this.debouncedSetPosition);
      });
    } else {
      this.dialog.removeEventListener('click', this.handleClick);
    }
  }

  setAriaAttributes() {
    this.dialog.setAttribute('role', 'dialog');
    this.dialog.setAttribute('aria-modal', 'true');
  }

  checkOutsideClick(event) {
    // Handle keyboard event (Enter or Space)
    if (event.clientY === 0 && event.clientX === 0) return false;

    // if inside other modals, return false
    if (event.target.closest('gtc-modal-content')) return false;

    const rect = this.dialog.getBoundingClientRect();

    return (
      event.clientY < rect.top ||
      event.clientY > rect.bottom ||
      event.clientX < rect.left ||
      event.clientX > rect.right
    );
  }

  // Open the modal inside an element, and scroll to it if it's not in view
  async openInsideElement() {

    if (!this.settings.openInside) return console.warn("Element to render inside not found");

    const isElementInView = (element) => {
      const rect = element.getBoundingClientRect();

      return (
        rect.top >= 0 &&
        rect.left >= 0 &&
        rect.bottom <= (window.innerHeight || document.documentElement.clientHeight) &&
        rect.right <= (window.innerWidth || document.documentElement.clientWidth)
      );
    };



    if (!isElementInView(this.settings.openInside)) {
      await new Promise(resolve => {
        this.settings.openInside.scrollIntoView({ behavior: "smooth", block: "center" });
        if (window.onscrollend !== undefined) {
          document.addEventListener("scroll", () => {
            document.addEventListener("scrollend", () => {
              resolve();
            }, { once: true });
          }, { once: true });
        } else {
          resolve();
        }
      });
    }

    this.setPosition();

    // if "this" if out of view, disable scroll lock
    if (!isElementInView(this.settings.openInside)) {
      this.#disableScrollLock = true;
    }
  }

  // Set the position of the modal inside the element
  setPosition = () => {
    const position = this.settings.openInside.getBoundingClientRect();
    const isFixed = getComputedStyle(this.settings.openInside).position === "fixed";

    if (!isFixed) {
      this.dialog.style.position = "absolute";
    }
    this.dialog.style.top = `${ position.top + (isFixed ? 0 : window.scrollY) }px`;
    this.dialog.style.left = `${ position.left }px`;
    this.dialog.style.width = `${ position.width }px`;
    this.dialog.style.height = `${ position.height }px`;
    this.dialog.style.margin = "0";
    this.dialog.style.maxWidth = "unset";
    this.dialog.style.maxHeight = "unset";
  }

  handleClick(event) {
    const target = event.target;
    const clickOutside = this.settings.closeOutside && this.checkOutsideClick(event);

    // Check if the clicked element has data-close attribute
    const hasCloseAttribute = target.dataset.close !== undefined && this.dialog.contains(target);

    // Check if any element in the event path has data-close attribute
    // This handles shadow DOM and nested elements
    const eventPath = event.composedPath ? event.composedPath() : [target];
    const hasCloseInPath = eventPath.some(element =>
      element && element.dataset && element.dataset.close !== undefined && this.dialog.contains(target)
    );

    const shouldClose = hasCloseAttribute || hasCloseInPath || clickOutside;
    if (shouldClose) this.close();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    }
  }

  async open() {
    if (!this.dialog) {
      console.warn('No dialog element found in gtc-modal');
      return;
    }

    if (this.settings.scripts && !this.#scriptsLoaded) {
      gtcLoadScripts(this.settings.scripts.split(','));
      this.#scriptsLoaded = true;
    }

    this.addOpenElementListeners();

    // Show the overlay, this is used to only show 1 overlay at a time
    this.showOverlay();

    // Open the modal inside an element, and scroll to it if it's not in view
    if (this.settings.openInside) await this.openInsideElement();

    // Lock the scroll if the modal is open if scrollLock if not disabled by openInsideElement
    if (!this.#disableScrollLock) {
      if (this.settings.scrollLock) gtcScrollLock([classes.scrollLock]);
    }

    // Reset the scroll lock disable flag 
    this.#disableScrollLock = false;

    // Use the dialog's showModal method
    if (this.dialog.showModal) {
      if (this.settings.openInside && getComputedStyle(this.settings.openInside).position !== "fixed") {
        this.dialog.show();
      } else {
        this.dialog.showModal();
      }
    } else {
      // Fallback for browsers without showModal support
      this.dialog.setAttribute('open', '');
      document.body.classList.add('dialog-open');
    }

    //This forces repaint
    void this.dialog.offsetHeight;

    requestAnimationFrame(() => {
      this.dialog.classList.add(classes.animate);
    });
  }

  close() {
    if (!this.dialog) {
      console.warn('No dialog element found in gtc-modal');
      return;
    }

    this.removeOpenElementListeners();

    this.hideOverlay();

    gtcTransitionEnd(this.dialog, () => {
      // Reset the scroll position to the top of the content
      this.content.shadowRoot?.querySelector('[part="body"]')?.scrollTo({ top: 0, behavior: "instant" });

      // Use the dialog's close method
      if (this.dialog.close) {
        this.dialog.close();
      } else {
        // Fallback for browsers without close support
        this.dialog.removeAttribute('open');
        document.body.classList.remove('dialog-open');
      }

    });

    this.dialog.classList.remove(classes.animate);

    // Only remove scrolllock if the modal is the last modal open, with scrollLock enabled
    if (this.settings.scrollLock) {
      const openModals = document.querySelectorAll('[data-scroll-lock] dialog[open]');
      if (openModals.length === 1) {
        gtcScrollUnlock([classes.scrollLock]);
      }
    }
  }

  showOverlay() {
    if (this.dialog.classList.contains(classes.overlay)) {
      if (!document.querySelector(`.${ classes.showOverlay }`)) {
        this.dialog.classList.add(classes.showOverlay);
      }
    }
  }

  hideOverlay() {
    this.dialog.classList.remove(classes.showOverlay);
  }
}

// Register as a standalone custom element instead of extending dialog
if (!customElements.get('gtc-modal')) {
  customElements.define('gtc-modal', GtcModal);
}

class GtcModalContent extends HTMLElement {
  constructor() {
    super();
    this.attachShadow({ mode: 'open' });
    this.settings = {
      canonicalUrl: this.getAttribute("canonical-url") || "",
    };
  }

  get shadowDomTemplate() {
    return this.dataset.template || "gtc-modal-template";
  }

  connectedCallback() {
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.render();
          observer.disconnect();
        }
      });
    });
    observer.observe(this);
  }

  async getSlotContent() {
    const slots = this.querySelectorAll("[slot]");

    if (!slots.length) return;
    const contentDataPromises = Array.from(slots).map(async (slot) => {
      for (const { name: attributeName, value: attributeValue } of slot.attributes) {
        switch (attributeName) {
          case "data-page":
            slot.insertAdjacentHTML("beforeend", await getPageContentHtml(attributeValue));
            break;
          case "data-section":
            slot.insertAdjacentHTML("beforeend", await getSectionHtml(attributeValue, this.settings.canonicalUrl));
            break;
          case "data-template":
            slot.insertAdjacentHTML("beforeend", await getTemplateHtml(attributeValue));
            break;
          default:
            break;
        }
      }
    });

    await Promise.all(contentDataPromises);
  }

  async render() {

    // If the opener has a shadow dom template, attach the shadow dom
    const templateDom = document.getElementById(this.shadowDomTemplate);
    if (templateDom) {
      this.shadowRoot.appendChild(templateDom.content.cloneNode(true));
    } else {
      console.warn("Shadow DOM could not be attached: template not found");
    }

    await this.getSlotContent();

    this.shadowRoot.querySelectorAll("slot").forEach((slot) => {
      // if slot is empty remove it
      if (!slot.assignedElements({ flatten: true }).length) {
        slot.remove();
      }
    });

    this.shadowRoot
      .querySelectorAll('[part="content"] [part]:not([part="header"] [part])')
      .forEach((part) => {
        // if part is empty remove it
        if (!part.children.length) {
          part.remove();
        }
      });

    if (!this.innerHTML) return console.warn("No HTML in gtc-modal-content!");

    if (this.settings?.class) {
      const classes = this.settings.class
        .split(" ")
        .filter((letter) => letter !== "");
      this.classList.add(...classes);
    }  
  }
}

if (!customElements.get('gtc-modal-content')) {
  customElements.define('gtc-modal-content', GtcModalContent,);
}

const gtcModal = class GtcModalElement extends GtcModal {
  constructor() {
    super();
  }
}


const gtcModalLinks = class extends HTMLElement {
  constructor() {
      super();
      this.checkLinks = this.checkLinks.bind(this);
  }

  connectedCallback() {
      this.dom = {};
      this.settings = {
          template: this.getAttribute("template") || "gtc-modal-template",
          templateClasses: this.getAttribute("template-classes") || "",
          appendToElement: this.hasAttribute("append-to-element")
              ? this.getAttribute("append-to-element") || ""
              : null,
          scrollLock: this.getAttribute("scroll-lock") === "false" ? false : true,
      };

      // if DOMContentLoaded has already happened
      if (
          document.readyState === "complete" ||
          document.readyState === "interactive"
      ) {
          this.init();
      } else {
          document.addEventListener("DOMContentLoaded", () => {
              this.init();
          });
      }
  }

  init() {
      if (this.settings.appendToElement) {
          this.settings.appendElementToContainer = document.querySelector(this.settings.appendToElement);
      }

      this.checkLinks();

      this.addEventListener("click", this.checkIfClickIsTrigger);
  }

  disconnectedCallback() {
      this.removeEventListener("click", this.checkIfClickIsTrigger);
  }

  checkLinks() {
      const baseUrl = window.location.origin;
      this.querySelectorAll("a").forEach((link) => {
          const url = link.getAttribute("href");

          if (url.includes(baseUrl) || url.startsWith("/")) {
              if (url.includes("policies/") || url.includes("pages/")) {
                  this.tranformToOpener(link);
              }
          }
      });
  }

  checkIfClickIsTrigger(e) {
      if (e.target.closest('gtc-trigger')) {
          e.preventDefault();
      }
  }

  async tranformToOpener(link) {
      const randomId = `Modal-${ Date.now() }`;
      const linkUrl = link.getAttribute("href");

      // Trigger
      const trigger = document.createElement("button");
      trigger.innerHTML = link.innerHTML;
      trigger.classList.add("inline-link");
      trigger.setAttribute("aria-controls", randomId);
      trigger.setAttribute("tabindex", 0);
      trigger.setAttribute("role", "button");
      trigger.setAttribute("aria-label", link.title);
      trigger.setAttribute("title", link.title);
      trigger.setAttribute("onclick", `document.getElementById('${ randomId }').open();`);

      link.replaceWith(trigger);

      // Get modal template
      const modalTemplate = this.querySelector("template").content;
      const modalTemplateContent = modalTemplate.children[0];

      // Modal
      const modal = modalTemplateContent;
      modal.id = randomId;

      const slotTitle = modal.querySelector("[slot='title']");
      const slotBody = modal.querySelector("[slot='body']");

      slotTitle.innerHTML = link.title;

      if (linkUrl) {
          slotBody.setAttribute("data-page", linkUrl);
      }

      this.parentElement.appendChild(modal);
  }
};

if (!window.customElements.get("gtc-modal-links")) {
  window.customElements.define("gtc-modal-links", gtcModalLinks);
}

export { gtcModal };